(ns app.front.view.group.admin.letter.template-editor
  (:require [app.domain.letter.model :as letter-model]
            [app.domain.letter.templates-config :as letter-templates-config]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.file.download :as file-download]
            [app.front.ui :as ui]
            [app.front.ui.components :as uic]
            [app.front.ui.select :as ui-select]
            [app.front.ui.text-editor :as text-editor]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.letter.bill-snippet-editor :as bill-snippet-editor]
            [app.front.view.group.admin.letter.bond-snippet-editor :as bond-snippet-editor]
            [app.front.view.group.admin.letter.condition-snippet-editor :as condition-snippet-editor]
            [app.front.view.group.admin.letter.inspection-snippet-editor :as inspection-snippet-editor]
            [app.front.view.group.admin.letter.party-snippet-editor :as party-snippet-editor]
            [app.front.view.group.admin.letter.violation-snippet-editor :as violation-snippet-editor]
            [app.front.view.group.admin.review-config :as review-config]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.templates.document-editor :as document-editor]
            [app.front.view.group.admin.templates.language-selector :as lang-sel]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [re-frame.core-instrumented :as rf])
  (:require-macros [core.util.util :as util]))

(defn set-attr-evt [template-id attr value]
  (if (keyword? template-id)
    ;; creating new
    [::ee/set-value template-id attr value]
    ;; editing existing, debounce with entity-edit
    [::ee/save-debounce
     nil
     :letter/edit-template
     template-id
     attr
     value
     {:notify {:success :template-editor/template-saved}}]))

(rf/reg-event-fx ::set-attr
  (fn [_ [_ template-id attr value]]
    {:dispatch (set-attr-evt template-id attr value)}))

(rf/reg-sub ::unsaved
  :<- [::ee/unsaved-for ::new]
  (fn [unsaved _]
    unsaved))

(rf/reg-event-fx ::create
  (fn [{:keys [db]} [_ group-id data]]
    {:comm/command {:command          :letter/create-template
                    :data             (-> data
                                          (assoc :group-id group-id))
                    :method           :post
                    :on-success       [::create.success group-id]
                    :finally-dispatch [::create.done]
                    :notify           {:success :letter/template-created}}
     :db           (assoc db ::creating-letter-template? true)}))

(rf/reg-event-fx ::create.success
  (fn [_ [_ group-id status changes]]
    {:fx            [[:dispatch [::entities/apply-changes status changes]]
                     ;; saved, discard changes from app-db
                     [:dispatch [::ee/cancel-changes ::new]]]
     :route/replace {:route  :view/group.admin.template
                     :params {:template-id (entities/id-of-some-in-apply-changes :entity.type/letter-pdf-template
                                                                                 changes)
                              :group-id    group-id}}}))

(rf/reg-event-db ::create.done
  (fn [db _]
    (dissoc db ::creating-letter-template?)))

(rf/reg-sub ::creating-letter-template?
  (fn [db _]
    (-> db ::creating-letter-template? true?)))

(rf/reg-sub ::template-type
  (fn [_ [_ document-id]]
    (or (<sub [::ee/attr-value document-id :letter-pdf-template/type])
        :letter-pdf-template.type/workspace)))

(defn create-controls [{:keys [tr group-id]}]
  (let [unsaved   (<sub [::unsaved])
        creating? (<sub [::creating-letter-template?])]
    [:div.mt-4
     (if creating?
       (cc/loading-spinner {:with-overlay false
                            :small        true})
       (cc/button {:on-click #(>evt [::create group-id unsaved])
                   :disabled (or
                               (str/blank? (:letter-pdf-template/name unsaved))
                               (nil? (:letter-pdf-template/type unsaved)))}
                  (tr [:create])))]))

(defn get-external-edit-state-key [fragment-type]
  (keyword (namespace ::x) (name fragment-type)))

(defn view [tr group-id template]
  (let [images                     (<sub [::gas/verdict-images group-id])
        document-id                (:db/id template ::new)
        permit-type                (<sub [::gas/group-primary-permit-type group-id])
        template-type              (<sub [::template-type document-id])
        group                      (<sub [::gas/group])
        group-features             (set (<sub [::gas/group-features]))
        feature-flags              (set (<sub [::ff/enabled-flags]))
        fragment-types             (->> template
                                        :document/fragments
                                        (map :document-fragment/type))
        visibility-ctx             (util/mmap template-type permit-type group group-features feature-flags)
        visible-fragment-types     (->> fragment-types
                                        (filter #(letter-templates-config/letter-fragment-applicable? % visibility-ctx))
                                        set)
        review-placeholder-options {:review-configs (<sub [::review-config/available-review-configs group-id])}
        html-template-variant      (->> fragment-types
                                        (filter #{:letter-fragment/html-template})
                                        first)
        inherited?                 (boolean (some-> template :domain/authority-group :db/id (not= group-id)))
        snippet-editor-ctx         (util/mmap get-external-edit-state-key visible-fragment-types group inherited?
                                              permit-type group-id document-id images group-features feature-flags tr)]
    [acommon/section {:title (tr [:letter/template])}
     [:div.flex.justify-between
      [uic/form-field-wide
       [ui/text-input {:label-text (tr [:letter/name])
                       :class      "mr-2"
                       :disabled   inherited?
                       :value      (<sub [::ee/attr-value document-id :letter-pdf-template/name])
                       ;; no fx-dispatch with dispatch-sync, otherwise text input on phones breaks
                       :on-change  #(rf/dispatch-sync (set-attr-evt document-id
                                                                    :letter-pdf-template/name
                                                                    (futil/tval %)))
                       :required   true}]
       [ui-select/select
        {:allow-empty? false
         :disabled     inherited?
         :values       (letter-templates-config/letter-pdf-template-type-choice permit-type)
         :id           "letter-template-type-select"
         :label-text   (tr [:letter/template-type])
         :val->str     #(tr [%])
         :class        "full-width-select pt-3"
         :on-change    #(rf/dispatch-sync (set-attr-evt document-id
                                                        :letter-pdf-template/type
                                                        %))
         :value        (<sub [::ee/attr-value document-id :letter-pdf-template/type])
         :required     true}]
       (when (<sub [::lang-sel/show-selector?])
         [:div.w-1-3
          [lang-sel/language-selector {:language  (<sub [::ee/attr-value document-id :entity/language])
                                       :on-change #(>evt [::set-attr document-id :entity/language %])}]])]]
     (if (keyword? document-id)
       ;; creating new, ask name and lang first
       [create-controls (util/mmap tr group-id)]
       ;; modifying existing
       [document-editor/editor
        (-> (util/mmap {:title              [:h3 (tr [:letter/template])]
                        :read-only?         inherited?
                        :preview-url        (file-download/download-href
                                              :group/generate-letter-template-preview-pdf
                                              {:group-id    group-id
                                               :template-id document-id})
                        :placeholders       (letter-model/applicable-template-variables visibility-ctx)
                        :html-fragment-type (or html-template-variant :letter-fragment/html-template)
                        :css-fragment-type  :letter-fragment/print-css}
                       tr images group-id document-id review-placeholder-options)
            (text-editor/with-external-edit-state ::main))
        [:<>
         [party-snippet-editor/party-snippets-editor snippet-editor-ctx]
         [violation-snippet-editor/violation-snippets-editor snippet-editor-ctx]
         [inspection-snippet-editor/inspection-snippets-editor snippet-editor-ctx]
         [bill-snippet-editor/bill-snippets-editor snippet-editor-ctx]
         [condition-snippet-editor/condition-snippets-editor snippet-editor-ctx]
         [bond-snippet-editor/bond-snippets-editor snippet-editor-ctx]]])]))
